import { catalogItem } from '../config/catalog';
import { ENTRANCE, PARCELS, PATH_CELLS, SLOTS, WAYPOINTS, type SlotDef } from '../config/map';
import { approvedPalettePreset, resolveApprovedPaletteId } from '../sim/appearance';
import {
  compatibilitySlotIdForParcel,
  parcelBuildableBounds,
  plotIdForLegacyCell,
  queryBuildableSubcell,
  type IslandParcelId,
} from '../spatial/buildableArea';
import { hasMacroEntitlement, islandPlots, landOwnsPlot, plotById } from '../spatial/plots';
import { candidateWorldPosition, createCandidatePlacement } from '../spatial/placementMigration';
import { entityAnchor, entityRotation, legacyCellOfAnchor, productionFootprint } from '../sim/placement';
import { applyAttractionPalette, type ChannelColorMap } from '../spikes/threejs/palettes';
import { floorOverrideMap, resolvedFloorStyle } from '../sim/floors';
import { visitorRoutePolylines } from '../game/walkRoutes';
import { visiblePathFloorCoverage } from '../spatial/surfaces';
import {
  FOUNDATION_DIVISION,
  createSubgridSpec,
  legacyCellSubcellCenters,
  subcellWorldCenter,
  worldToSubgrid,
} from '../spatial/subgrid';
import type { EntityKind, FloorStyleId, ParkState, PlacedEntity, RotationQuarter } from '../types';

/** Frozen 5x5 terrain foundation. Entity poses still use `gridToWorld`, not these centers. */
export const TERRAIN_SUBGRID_SPEC = createSubgridSpec(FOUNDATION_DIVISION);

export const CELL_SIZE = 2.4;

export const CATALOG_VISUAL_BY_KIND = {
  starter_ride: 'carousel',
  spinning_ride: 'spinningRide',
  pirate_ship: 'pirateShip',
  ferris_wheel: 'ferrisWheel',
  roller_coaster: 'coaster',
  snack_kiosk: 'snackKiosk',
  drink_kiosk: 'drinkKiosk',
  bench: 'bench',
  tree: 'tree',
  lamp: 'lamp',
} as const;

export type CatalogVisual = (typeof CATALOG_VISUAL_BY_KIND)[EntityKind];
export type MappedVisual = CatalogVisual | 'placeholder';

export interface WorldPoint {
  x: number;
  z: number;
}

export interface EntityRenderSpec {
  id: string;
  kind: EntityKind;
  slotId: string;
  level: number;
  visual: MappedVisual;
  label: string;
  x: number;
  z: number;
  rotation: RotationQuarter;
  /** Resolved approved Fable palette id for supported kinds. */
  paletteId?: string;
}

export interface SlotRenderSpec {
  slotId: string;
  parcelId: string;
  col: number;
  row: number;
  x: number;
  z: number;
  occupied: boolean;
  /** Logical 5x5 centers for terrain only. Selection/build still use `x`/`z`. */
  subcells: WorldPoint[];
}

export type LandPreviewKind = 'owned' | 'distant' | 'frontier' | 'recommended';

export interface LandRenderSpec {
  parcelId: string;
  label: string;
  unlocked: boolean;
  x: number;
  z: number;
  width: number;
  depth: number;
  /** COL-010: guidance target among the visible frontier overlays. */
  recommended?: boolean;
  /** COL-010-C2: Land-mode classification for every atomic island plot. */
  previewKind?: LandPreviewKind;
}

export interface PathFloorCellSpec {
  sx: number;
  sz: number;
  x: number;
  z: number;
  role: 'entrance' | 'path' | 'custom';
  /** Any paintable style except `grass` (grass cells are simply not emitted). */
  style: Exclude<FloorStyleId, 'grass'>;
  /** True when the cell sits on a contextual lawn pad and must render above it. */
  coversLawn: boolean;
}

export interface MigrationParity {
  coins: number;
  entityCount: number;
  unlockedLandIds: string[];
  entities: Array<{ kind: EntityKind; slotId: string; level: number }>;
  placeholderCount: number;
}

export interface MigrationRenderModel {
  entities: EntityRenderSpec[];
  slots: SlotRenderSpec[];
  lands: LandRenderSpec[];
  pathCells: WorldPoint[];
  entrance: WorldPoint;
  /** Visible 0.48 paving cells: entrance 5x5 + centered 3-wide path band + paints. */
  pathFloorCells: PathFloorCellSpec[];
  /** Canonical WAYPOINTS in the same gridToWorld space as paths/slots. */
  waypoints: WorldPoint[];
  /** Closed visitor polylines derived from the walkable floor graph. */
  visitorRoutes: WorldPoint[][];
  parity: MigrationParity;
  selectedSlotId?: string | null;
  /** Strengthens the 5x5 overlay in Build; omitted/false stays the subtle normal cue. */
  buildMode?: boolean;
  /** COL-010-C1: Land mode shows the full locked-island preview. */
  landMode?: boolean;
}

export function worldCenterForSubcell(coord: { sx: number; sz: number }): WorldPoint {
  return subcellWorldCenter(coord, TERRAIN_SUBGRID_SPEC);
}

export function gridToWorld(col: number, row: number): WorldPoint {
  return {
    x: (col - 2) * CELL_SIZE,
    z: (row - 1.5) * CELL_SIZE,
  };
}

export function visualForKind(kind: EntityKind): MappedVisual {
  return CATALOG_VISUAL_BY_KIND[kind] ?? 'placeholder';
}

/** Recolor existing channel materials from persisted approved palette IDs. Does not clone meshes. */
export function applyApprovedPalettesToChannels(
  model: MigrationRenderModel,
  carouselChannels: ChannelColorMap,
  coasterChannels: ChannelColorMap,
): { carouselPaletteId: string; coasterPaletteId: string } {
  const carousel = model.entities.find((entity) => entity.kind === 'starter_ride');
  const coaster = model.entities.find((entity) => entity.kind === 'roller_coaster');
  const carouselPreset = approvedPalettePreset('starter_ride', carousel?.paletteId)!;
  const coasterPreset = approvedPalettePreset('roller_coaster', coaster?.paletteId)!;
  applyAttractionPalette(carouselChannels, carouselPreset.palette);
  applyAttractionPalette(coasterChannels, coasterPreset.palette);
  return {
    carouselPaletteId: carouselPreset.id,
    coasterPaletteId: coasterPreset.id,
  };
}

export function mapEntity(entity: PlacedEntity, _slot: SlotDef): EntityRenderSpec {
  const placement = createCandidatePlacement({
    id: entity.id,
    kind: entity.kind,
    anchor: entityAnchor(entity),
    rotation: entityRotation(entity),
    spec: productionFootprint(entity.kind),
    originSlotId: entity.slotId,
  });
  const world = candidateWorldPosition(placement);
  const visual = visualForKind(entity.kind);
  const paletteId = resolveApprovedPaletteId(entity.kind, entity.appearance?.paletteId);
  return {
    id: entity.id,
    kind: entity.kind,
    slotId: entity.slotId,
    level: entity.level,
    visual,
    label: catalogItem(entity.kind).name,
    x: world.x,
    z: world.z,
    rotation: entityRotation(entity),
    ...(paletteId ? { paletteId } : {}),
  };
}

function landExtent(parcelId: string): { x: number; z: number; width: number; depth: number } {
  const plot = plotById(parcelId);
  if (plot) {
    return { x: plot.center.x, z: plot.center.z, width: plot.width, depth: plot.depth };
  }
  return { x: 0, z: 0, width: 2.4, depth: 2.4 };
}

function lockedCellContains(slot: SlotDef, x: number, z: number): boolean {
  const world = gridToWorld(slot.col, slot.row);
  const half = CELL_SIZE / 2;
  return Math.abs(x - world.x) <= half && Math.abs(z - world.z) <= half;
}

/** Hit-test locked parcels from canonical slot cells, then the expanded canvas. */
export function resolveLockedLandHit(
  model: MigrationRenderModel,
  x: number,
  z: number,
): { parcelId: string; slotId: string } | null {
  const unlocked = new Set(model.lands.filter((land) => land.unlocked).map((land) => land.parcelId));
  let best: SlotDef | null = null;
  let bestDist = Infinity;
  for (const slot of SLOTS) {
    const slotPlot = plotIdForLegacyCell(slot.col, slot.row);
    if (unlocked.has(slotPlot) || unlocked.has(slot.parcelId) || !lockedCellContains(slot, x, z)) {
      continue;
    }
    const world = gridToWorld(slot.col, slot.row);
    const dist = Math.hypot(x - world.x, z - world.z);
    if (dist < bestDist) {
      bestDist = dist;
      best = slot;
    }
  }
  if (best) {
    return { parcelId: best.parcelId, slotId: best.id };
  }
  const query = queryBuildableSubcell(worldToSubgrid(x, z, TERRAIN_SUBGRID_SPEC), []);
  if (query.insideCanvas && query.plotId && !unlocked.has(query.plotId) && query.parcelId && !unlocked.has(query.parcelId)) {
    return { parcelId: query.parcelId, slotId: compatibilitySlotIdForParcel(query.parcelId) };
  }
  return null;
}

/** Existing orange-ring anchor: unlocked slot center, or locked parcel center. Land mode never uses this ring. */
export function resolveSelectionAnchor(
  model: MigrationRenderModel,
): (WorldPoint & { slotId: string }) | null {
  if (model.landMode) {
    return null;
  }
  const selectedSlotId = model.selectedSlotId;
  if (!selectedSlotId) {
    return null;
  }
  const unlocked = model.slots.find((slot) => slot.slotId === selectedSlotId);
  if (unlocked) {
    return { x: unlocked.x, z: unlocked.z, slotId: unlocked.slotId };
  }
  const def = SLOTS.find((slot) => slot.id === selectedSlotId);
  if (!def) {
    return null;
  }
  const land = model.lands.find((item) => item.parcelId === def.parcelId);
  if (!land || land.unlocked) {
    return null;
  }
  return { x: land.x, z: land.z, slotId: selectedSlotId };
}

export function mapParkStateToRender(state: ParkState): MigrationRenderModel {
  const bySlot = new Map<string, PlacedEntity>();
  for (const entity of state.entities) {
    if (!bySlot.has(entity.slotId)) {
      bySlot.set(entity.slotId, entity);
    }
  }
  const entities: EntityRenderSpec[] = [];
  for (const entity of state.entities) {
    const slot = SLOTS.find((item) => item.id === entity.slotId);
    if (!slot) {
      continue;
    }
    entities.push(mapEntity(entity, slot));
  }

  const slots: SlotRenderSpec[] = SLOTS.filter((slot) =>
    landOwnsPlot(state.unlockedLandIds, plotIdForLegacyCell(slot.col, slot.row)),
  ).map(
    (slot) => {
      const world = gridToWorld(slot.col, slot.row);
      return {
        slotId: slot.id,
        parcelId: slot.parcelId,
        col: slot.col,
        row: slot.row,
        x: world.x,
        z: world.z,
        occupied: state.entities.some((entity) => {
          const cell = legacyCellOfAnchor(entityAnchor(entity));
          return cell.col === slot.col && cell.row === slot.row;
        }),
        subcells: legacyCellSubcellCenters(slot.col, slot.row, TERRAIN_SUBGRID_SPEC),
      };
    },
  );

  const lands: LandRenderSpec[] = [
    ...islandPlots().map((plot) => ({
      parcelId: plot.id,
      label: plot.id,
      unlocked: landOwnsPlot(state.unlockedLandIds, plot.id),
      ...landExtent(plot.id),
    })),
    ...PARCELS.map((parcel) => ({
      parcelId: parcel.id,
      label: parcel.label,
      unlocked: hasMacroEntitlement(state.unlockedLandIds, parcel.id),
      ...parcelBuildableBounds(parcel.id as IslandParcelId),
    })),
  ];

  return {
    entities,
    slots,
    lands,
    pathCells: PATH_CELLS.map((cell) => gridToWorld(cell.col, cell.row)),
    entrance: gridToWorld(ENTRANCE.col, ENTRANCE.row),
    pathFloorCells: (() => {
      const overrides = floorOverrideMap(state);
      const cells: PathFloorCellSpec[] = [];
      const seen = new Set<string>();
      const push = (sx: number, sz: number, role: PathFloorCellSpec['role']) => {
        const style = resolvedFloorStyle({ sx, sz }, overrides);
        if (!style || style === 'grass') {
          return;
        }
        const key = `${sx},${sz}`;
        if (seen.has(key)) {
          return;
        }
        seen.add(key);
        const world = subcellWorldCenter({ sx, sz }, TERRAIN_SUBGRID_SPEC);
        const coversLawn = role === 'custom';
        cells.push({
          sx,
          sz,
          x: world.x,
          z: world.z,
          role,
          style,
          coversLawn,
        });
      };
      for (const cell of visiblePathFloorCoverage(ENTRANCE, PATH_CELLS)) {
        push(cell.coord.sx, cell.coord.sz, cell.role);
      }
      for (const cell of state.floors ?? []) {
        push(cell.sx, cell.sz, 'custom');
      }
      return cells;
    })(),
    waypoints: WAYPOINTS.map((cell) => gridToWorld(cell.col, cell.row)),
    visitorRoutes: visitorRoutePolylines(
      state,
      WAYPOINTS.map((cell) => gridToWorld(cell.col, cell.row)),
    ),
    parity: {
      coins: state.coins,
      entityCount: state.entities.length,
      unlockedLandIds: [...state.unlockedLandIds],
      entities: state.entities.map((entity) => ({
        kind: entity.kind,
        slotId: entity.slotId,
        level: entity.level,
      })),
      placeholderCount: entities.filter((entity) => entity.visual === 'placeholder').length,
    },
  };
}

export function classifyLockedPlotPreview(
  plotId: string,
  frontierPlotIds: readonly string[],
  recommendedPlotIds: readonly string[] = [],
): Exclude<LandPreviewKind, 'owned'> {
  if (recommendedPlotIds.includes(plotId)) {
    return 'recommended';
  }
  if (frontierPlotIds.includes(plotId)) {
    return 'frontier';
  }
  return 'distant';
}

/** Owned plots are their own class; still-locked plots reuse C1 locked rules. */
export function classifyIslandPlotPreview(
  plotId: string,
  owned: boolean,
  frontierPlotIds: readonly string[],
  recommendedPlotIds: readonly string[] = [],
): LandPreviewKind {
  if (owned) {
    return 'owned';
  }
  return classifyLockedPlotPreview(plotId, frontierPlotIds, recommendedPlotIds);
}

function stripLandPreview(land: LandRenderSpec): LandRenderSpec {
  return {
    parcelId: land.parcelId,
    label: land.label,
    unlocked: land.unlocked,
    x: land.x,
    z: land.z,
    width: land.width,
    depth: land.depth,
  };
}

/** Land mode classifies every atomic plot; idle/Build/Floor hide Land overlays. */
export function applyLandModeOverlay(
  model: MigrationRenderModel,
  options: {
    landMode: boolean;
    visiblePlotIds: readonly string[];
    recommendedPlotIds?: readonly string[];
  },
): MigrationRenderModel {
  const recommended = options.recommendedPlotIds ?? [];
  return {
    ...model,
    landMode: options.landMode,
    lands: model.lands
      .filter((land) => {
        if (!land.parcelId.startsWith('plot_') || land.unlocked) {
          return true;
        }
        return options.landMode;
      })
      .map((land) => {
        if (!land.parcelId.startsWith('plot_') || !options.landMode) {
          return stripLandPreview(land);
        }
        const previewKind = classifyIslandPlotPreview(
          land.parcelId,
          land.unlocked,
          options.visiblePlotIds,
          recommended,
        );
        return { ...land, previewKind, recommended: previewKind === 'recommended' };
      }),
  };
}
